import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LoadingService } from '@/core/services/loading.service';
import { User } from '@/core/models/user';
import { UserService } from '@/core/services/open-api/user.service';
import { OnboardingService } from './onboarding.service';
import { CrmWebhookEvent, CrmWebhookService } from '@/core/services/crm-webhook.service';
import { AccountSettingsForm } from '@/core/models/account';
import { SharedFunctions } from '@/core/utils/shared-functions';
@Component({
    selector: 'app-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
    standalone: false,
})
export class OnboardingComponent implements OnInit, OnDestroy {
    steps: boolean[] = [];
    name?: string;
    private me?: User;
    stepsDone = '';
    stepsProgress = 0;
    currentStep = 0;
    standard_of: string = 'of';
    stetpsSubscription?: Subscription;
    displayFirstStartDialog: boolean = false;

    constructor(
        private readonly crmWebhookService: CrmWebhookService,
        private readonly translate: TranslateService,
        private readonly userService: UserService,
        private readonly router: Router,
        private readonly onboardingService: OnboardingService,
        private readonly loadingService: LoadingService,
    ) {
        this.translate.get(['standard_of']).subscribe((translations) => {
            this.standard_of = translations.standard_of;
        });

        this.userService.currentUser$.subscribe((user) => {
            if (user) {
                this.name = user.firstName;
            }
        });
    }

    ngOnInit(): void {
        this.loadingService.setLoading(true);

        this.stetpsSubscription = this.onboardingService.steps$.subscribe((steps) => this.handleStepsChanged(steps));

        // this.displayFirstStartDialog = this.route.snapshot.queryParamMap.get('firstStart') === 'true'

        this.userService.loadCurrentUser().subscribe((me) => {
            this.me = me;
        });
    }

    ngOnDestroy(): void {
        this.stetpsSubscription?.unsubscribe();
        if (this.stepsProgress == 100) {
            this.finishOnboarding();
        }
    }

    private handleStepsChanged(steps: boolean[] | undefined): void {
        this.loadingService.setLoading(false);
        if (steps == undefined) {
            void this.router.navigate(['/']);
            return;
        }
        this.updateUI(steps);
    }

    private updateUI(steps: boolean[]): void {
        this.steps = steps;
        const doneSteps = this.steps.filter((value) => !!value).length;
        this.stepsDone = `${doneSteps} ${this.standard_of} ${this.steps.length}`;
        this.stepsProgress = (100 * doneSteps) / this.steps.length;
        this.currentStep = this.steps.findIndex((step) => !step);
    }

    finishOnboarding(): void {
        this.loadingService.setLoading(true);

        const settings: AccountSettingsForm = {
            onboarding: 'COMPLETED',
        };
        this.userService.patchAccountSettings(settings).subscribe();

        void this.crmWebhookService
            .send(CrmWebhookEvent.onboardingWizardCompleted, {
                email: this.me?.email,
            })
            .then(() => {
                this.loadingService.setLoading(false);
                void this.router.navigate(['/'], { queryParams: { firstStart: true } });
            });
    }

    linkToSupport(): void {
        this.translate.get(['language_parameter_store_link']).subscribe((translations) => {
            this.userService.getLicensesURL().subscribe((data) => {
                const domain = data.split('?');
                const newUrl = domain[0] + translations.language_parameter_store_link + '/support' + '?' + domain[1];
                // console.debug("newUrl: " + newUrl)
                window.open(newUrl, '_blank', 'noopener');
            });
        });
    }

    nextStep(): void {
        this.loadingService.setLoading(true);

        const temp = JSON.parse(JSON.stringify(this.steps));
        temp[this.currentStep] = true;
        const settings: AccountSettingsForm = {
            onboarding: JSON.stringify(temp),
        };

        this.userService.patchAccountSettings(settings).subscribe(() => {
            this.loadingService.setLoading(false);
            // incrementing is not needed here as the OnboardingService subscription recacluclates the currentStep
            // automatically. The following code is just for double safety.
            // Old code always incremented causing an increment on an already updated value.
            if (temp[this.currentStep] === true && temp[this.currentStep + 1] === false) {
                this.currentStep++;
            }
        });
    }

    getTranslatedKey(index: number): string {
        return `onboarding_step${index}`;
    }

    getVideoUrl(index: number): string {
        let url = '';
        this.translate
            .get(['onboarding_step2_video', 'onboarding_step3_video', 'onboarding_step4_video'])
            .subscribe((translations) => {
                url = translations['onboarding_step' + index + '_video'];
            });
        return url;
    }

    getButtonTitle(): string {
        return this.currentStep === 1 ? 'onboarding_button_go' : 'onboarding_button_next';
    }

    downloadApp(): void {
        SharedFunctions.startAppDownload();
    }

    linkToAddons(): void {
        void this.router.navigateByUrl('/settings/addons');
    }

    hideFirstStartDialog(): void {
        this.displayFirstStartDialog = false;
        // this.router.navigate(['/'])
    }
}
